/* Workshops & Guest Speakers: /workshops
   ----------------------------------------------------------------------
   One of the four main sections (owner, 2026-10-06), in the shared
   catalogue structure. Facts from the For Schools build prompt only:
   workshops at school, guest speakers, visits to companies (Kidsology
   organises, the school provides transport), parent talks and seminars,
   free for partner schools. Never publish speaker availability (build
   prompt, hard rule 4). Copy in src/data/offer.js.

   The enquiry writes to `partnershipLeads` like the /schools form, with
   partnerType "workshops" and the chosen formats leading the message. */

import { SIZES } from "../lib/img";
import React from "react";
import SEO from "../components/SEO";
import SiteHeader from "../components/SiteHeader";
import LocaleLink from "../components/LocaleLink";
import { useLocale, useL } from "../i18n/LocaleContext";
import { WORKSHOPS_PAGE, SECTION, CONTACTS } from "../data/offer";
import { SCHOOLS } from "../data/schools";
import { breadcrumbSchema, sectionServiceSchema, faqPageSchema } from "../data/schemas";
import { ARROW, Pic, CheckList, ThreeCards, FloatingCta, useHashScroll, Accent, SectionHead, PartnerEnquiryForm, ContactList, FaqList } from "../components/catalog/CatalogParts";
import { track } from "../lib/analytics";

const PATH = "/workshops";

export default function Workshops() {
  const { locale } = useLocale();
  const c = useL(WORKSHOPS_PAGE);
  const contacts = useL(CONTACTS);
  const name = useL(SECTION.workshops);
  useHashScroll();

  return (
    <div className="cat-page">
      <SEO
        title={c.seo.title}
        description={c.seo.description}
        path={PATH}
        image={`${WORKSHOPS_PAGE.hero.photos[0].src}.jpg`}
        schema={[
          sectionServiceSchema({
            path: PATH,
            name: SECTION.workshops,
            description: WORKSHOPS_PAGE.seo.description,
            serviceType: { en: "School workshops and guest speakers", ar: "ورش عمل مدرسية ومتحدثون ضيوف", ru: "Школьные воркшопы и приглашённые спикеры" },
          }, locale),
          breadcrumbSchema([
            { name: SCHOOLS.home, path: "/" },
            { name: SECTION.workshops, path: PATH },
          ], locale),
          faqPageSchema(WORKSHOPS_PAGE.faq.items, locale),
        ]}
      />
      <SiteHeader ctaHref="#enquire" />
      <main id="main">
        {/* ── Hero ── */}
        <header className="cat-hero">
          <div className="cat-hero-inner">
            <div className="cat-hero-copy">
              <p className="cat-eyebrow">{name}</p>
              <h1 className="cat-title"><Accent title={c.hero.title} /></h1>
              <p className="cat-lead">{c.hero.lead}</p>
              <ul className="cat-stats cat-stats--words">
                {c.hero.stats.map((s, i) => (
                  <li key={i}>
                    <span className="cat-stat-v">{s.v}</span>
                    <span className="cat-stat-l">{s.l}</span>
                  </li>
                ))}
              </ul>
              <div className="cat-actions cat-hero-actions">
                <a href="#formats" className="btn btn-gold-lg" onClick={() => track("workshops_hero_cta_click", { target: "formats" })}>{c.hero.ctaFormats}</a>
                <a href="#enquire" className="btn btn-outline-white" onClick={() => track("workshops_hero_cta_click", { target: "enquire" })}>{c.hero.ctaTalk}</a>
              </div>
            </div>
            <div className="cat-mosaic">
              {c.hero.photos.map((p, i) => <Pic key={p.src} src={p.src} alt={p.alt} eager={i < 2} sizes={SIZES.strip} />)}
            </div>
          </div>
        </header>

        {/* ── The three formats ── */}
        <section className="cat-section cat-section--white" id="formats" aria-labelledby="fm-title">
          <div className="cat-wrap">
            <SectionHead id="fm-title" eyebrow={c.formats.eyebrow} title={c.formats.title} />
            <p className="cat-pill-row"><span className="cat-pill cat-pill--green">{c.formats.pill}</span></p>
            <ThreeCards items={c.formats.cards} numbered />
            <p className="cat-line">{c.formats.note}</p>
          </div>
        </section>

        {/* ── Why schools choose Kidsology ── */}
        <section className="cat-section" aria-labelledby="why-title">
          <div className="cat-wrap cat-wrap--narrow">
            <div className="cat-panel cat-panel--accent">
              <h2 id="why-title" className="cat-panel-title">{c.why.title}</h2>
              <CheckList items={c.why.items} />
              <p className="cat-small">
                <LocaleLink to="/schools" className="cat-info-link">{c.why.schoolsLink} {ARROW}</LocaleLink>
              </p>
            </div>
          </div>
        </section>

        {/* ── Questions schools ask ── */}
        <section className="cat-section cat-section--white" id="faq" aria-labelledby="faq-title">
          <div className="cat-wrap cat-wrap--narrow">
            <SectionHead id="faq-title" eyebrow={c.faq.eyebrow} title={c.faq.title} />
            <FaqList items={c.faq.items} />
          </div>
        </section>

        {/* ── Enquiry ── */}
        <section className="cat-section cat-section--navy" id="enquire" aria-labelledby="enquire-title">
          <div className="cat-enquiry-inner">
            <div>
              <p className="cat-eyebrow">{c.enquiry.eyebrow}</p>
              <h2 id="enquire-title" className="cat-h2"><Accent title={c.enquiry.title} /></h2>
              <p className="cat-sub">{c.enquiry.lead}</p>
              <ContactList contacts={contacts} />
            </div>
            <div className="cat-form">
              <PartnerEnquiryForm copy={c.enquiry.form} options={c.enquiry.options} partnerType="workshops" event="workshops_enquiry_submit" idPrefix="ws" />
            </div>
          </div>
        </section>
      </main>
      <FloatingCta
        href="#enquire"
        label={c.floatCta}
        heroSelector=".cat-hero-actions"
        targetId="enquire"
        event="workshops_float_cta_click"
      />
    </div>
  );
}
